import { UserRole } from '@lmc/ts-common'
import { NAMESPACE } from 'locale'
import { reportingConstants } from 'locale/reporting'
import { reportsConstants } from 'locale/team'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { RoleRosterScope, WizardRole } from './types'

export type RoleOption = {
  value: WizardRole
  label: ReactNode
  disabled?: boolean
  tooltip?: string
}

// CM-3388: the group screen's "Add to Group Dashboard" path offers only the
// two group-dashboard roles (mirrors the legacy AddTeamMemberModal's
// `groupDashboardUserRoles`). All other scopes (single-site / specific-sites)
// share the existing site-level roster. RoleRosterScope is defined in ./types.
export type { RoleRosterScope }

export const useRoleOptions = (
  scope: RoleRosterScope = 'single-site'
): RoleOption[] => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { t: tReporting } = useTranslation(NAMESPACE.REPORTING)

  const viewer = useSelector(
    (s: { global: { user: { role: UserRole } } }) => s.global.user
  )

  // The wizard is already downstream of the `roles_permissions_v2` flag at
  // the TeamList level — Management and Audit View are part of the V2 role
  // model, so they always render here. The legacy `management_role` /
  // `audit_view_role` flags only gate the V1 paths (legacy modal,
  // TeamSideFilters, etc.) and are intentionally not consulted from V2.
  const viewerIsSenior = viewer?.role === UserRole.seniorCarer
  const viewerIsManagement = viewer?.role === UserRole.management

  const adminDisabledTooltip = t(
    reportsConstants.WIZARD_STEP2_ADMIN_DISABLED_TOOLTIP
  )
  const itDisabledTooltip = t(reportsConstants.WIZARD_STEP2_IT_DISABLED_TOOLTIP)

  // CM-3797: dropdown order per design —
  //   Admin → Management → Senior Carer → Carer → IT & Security → Audit View
  return useMemo(
    () =>
      scope === 'group-dashboard'
        ? [
            {
              value: UserRole.groupAdmin,
              label: tReporting(reportingConstants[UserRole.groupAdmin])
            },
            {
              value: UserRole.groupManager,
              label: tReporting(reportingConstants[UserRole.groupManager])
            }
          ]
        : [
            {
              value: UserRole.careHomeAdmin,
              label: tReporting(reportingConstants[UserRole.careHomeAdmin]),
              disabled: viewerIsSenior || viewerIsManagement,
              // Tooltip applies to both restricted viewers — Senior Carer can't
              // pick Admin either, so they need the same explanation.
              tooltip:
                viewerIsManagement || viewerIsSenior
                  ? adminDisabledTooltip
                  : undefined
            },
            {
              value: UserRole.management,
              label: tReporting(reportingConstants[UserRole.management]),
              disabled: viewerIsSenior
            },
            {
              value: UserRole.seniorCarer,
              label: tReporting(reportingConstants[UserRole.seniorCarer])
            },
            {
              value: UserRole.carer,
              label: tReporting(reportingConstants[UserRole.carer])
            },
            {
              value: UserRole.it,
              label: t(reportsConstants.ROLE_LABEL_IT_SECURITY),
              // CM-3863: Management can't manage the IT & Security role (the
              // backend blocks it), so disable the option for them too.
              disabled: viewerIsSenior || viewerIsManagement,
              tooltip: viewerIsManagement ? itDisabledTooltip : undefined
            },
            {
              value: UserRole.auditView,
              label: tReporting(reportingConstants[UserRole.auditView]),
              disabled: viewerIsSenior
            }
          ],
    [
      scope,
      t,
      tReporting,
      viewerIsSenior,
      viewerIsManagement,
      adminDisabledTooltip,
      itDisabledTooltip
    ]
  )
}
